import { StepContentInput, StepContentInputArea } from './styles';
import {
  ESplitPaymentAmountType,
  ESplitPaymentType,
  SplitPayment,
} from 'modules/products/interfaces/splitPayment';
import { BusinessEditSideSheet } from 'modules/customer/components/BusinessEditSideSheet';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TPreviewSimulationData } from 'modules/simulation/interfaces/context.interfaces';
import { getSplitAmount } from './helpers';
import {
  WarningList,
  WarningListIcon,
  WarningListItem,
  WarningListItems,
} from './WarningList';
import { AttentionRequiredDocuments } from 'app/components/Icons';
import { CustomInput, CustomSelect, TextL } from 'app/components';
import { formatCurrency } from 'app/utils/normalizer';

export interface ISplitPaymentEditSideSheet {
  handleSave: (splitPayments: SplitPayment[]) => void;
  handleClose: () => void;
  simulation: TPreviewSimulationData | null;
  splitPayment: SplitPayment[];
  isOpen: boolean;
}

enum EErrorType {
  ERROR_AMOUNT_MAX = 'ERROR_AMOUNT_MAX',
  ERROR_AMOUNT_MIN = 'ERROR_AMOUNT_MIN',
}

const SplitPaymentEditSideSheet = ({
  handleSave,
  handleClose,
  splitPayment,
  simulation,
  isOpen,
}: ISplitPaymentEditSideSheet) => {
  const [internalSplitPayments, setInternalSplitPayments] =
    useState<SplitPayment[]>(splitPayment);
  const [error, setError] = useState<EErrorType | null>(null);

  useEffect(() => {
    setInternalSplitPayments(splitPayment);
    setError(null);
  }, [splitPayment, isOpen]);

  const onSave = () => {
    if (!simulation || !simulation.requested_amount) return;

    const splitPaymentParsed = internalSplitPayments.map((item) => ({
      ...item,
      amount: parseFloat(
        item.amountString?.replaceAll('.', '')?.replace(',', '.') ?? '0',
      ),
    }));

    const total = splitPaymentParsed.reduce((acc, item) => {
      return acc + getSplitAmount(item, simulation);
    }, 0);

    if (total > simulation.requested_amount) {
      setError(EErrorType.ERROR_AMOUNT_MAX);
      return;
    }
    if (total < simulation.requested_amount) {
      setError(EErrorType.ERROR_AMOUNT_MIN);
      return;
    }

    setError(null);
    handleSave(splitPaymentParsed);
  };
  const onClose = () => {
    handleClose();
  };

  const onChangeAmount = useCallback((item: SplitPayment, value: string) => {
    setInternalSplitPayments((prev) =>
      prev.map((prevItem) => {
        if (prevItem.taxpayer_id === item.taxpayer_id) {
          return {
            ...prevItem,
            amountString: value,
          };
        }
        return prevItem;
      }),
    );
  }, []);

  const onChangeAmountType = useCallback(
    (amountType: ESplitPaymentAmountType) => {
      setInternalSplitPayments((prev) =>
        prev.map((prevItem) => ({
          ...prevItem,
          amount_type: amountType,
        })),
      );
    },
    [],
  );

  const borrowerSplit = useMemo(
    () =>
      internalSplitPayments.find(
        (item) => item.type === ESplitPaymentType.BORROWER,
      ),
    [internalSplitPayments],
  );

  const beneficiarySplit = useMemo(
    () =>
      internalSplitPayments.find(
        (item) => item.type === ESplitPaymentType.BENEFICIARY,
      ),
    [internalSplitPayments],
  );

  const selectedAmountType =
    borrowerSplit?.amount_type ?? ESplitPaymentAmountType.MONEY;

  const amountTypeOptions = useMemo(
    () => [
      {
        label: 'Monetário',
        value: ESplitPaymentAmountType.MONEY,
      },
      {
        label: 'Porcentagem',
        value: ESplitPaymentAmountType.PERCENT,
      },
    ],
    [],
  );

  return (
    <BusinessEditSideSheet
      open={isOpen}
      handleClose={onClose}
      handleSave={onSave}
      isLoading={false}
      title="Editar valor do split"
    >
      <StepContentInputArea>
        <StepContentInput isFull={true}>
          <CustomSelect
            labelValue="Tipo de valor"
            name="splitAmountType"
            value={
              amountTypeOptions.find(
                (option) => option.value === selectedAmountType,
              ) ?? amountTypeOptions[0]
            }
            options={amountTypeOptions}
            handleInputChange={(event) => {
              const option = event?.currentTarget?.value;
              if (!option) return;
              onChangeAmountType(option.value as ESplitPaymentAmountType);
            }}
          />
        </StepContentInput>

        {borrowerSplit && (
          <StepContentInput isFull={true}>
            <CustomInput
              labelValue={
                selectedAmountType === ESplitPaymentAmountType.PERCENT
                  ? 'Valor para a conta do tomador (%)'
                  : 'Valor para a conta do tomador (R$)'
              }
              value={borrowerSplit.amountString ?? ''}
              handleInputChange={(event) =>
                onChangeAmount(borrowerSplit, event.currentTarget.value)
              }
            />
          </StepContentInput>
        )}

        {beneficiarySplit && (
          <StepContentInput isFull={true}>
            <CustomInput
              labelValue={
                selectedAmountType === ESplitPaymentAmountType.PERCENT
                  ? `Valor para a conta do beneficiário (${beneficiarySplit.name}) (%)`
                  : `Valor para a conta do beneficiário (${beneficiarySplit.name}) (R$)`
              }
              value={beneficiarySplit.amountString ?? ''}
              handleInputChange={(event) =>
                onChangeAmount(beneficiarySplit, event.currentTarget.value)
              }
            />
          </StepContentInput>
        )}
      </StepContentInputArea>
      {error === EErrorType.ERROR_AMOUNT_MIN && (
        <WarningList>
          <WarningListIcon>
            <AttentionRequiredDocuments width={26} height={26} />
          </WarningListIcon>
          <TextL weight="bold">Valor solicitado insuficiente.</TextL>
          <WarningListItems>
            <WarningListItem>
              A soma do valor solicitado deve ser 100% (
              {formatCurrency(simulation!.requested_amount!, true)}).
            </WarningListItem>
          </WarningListItems>
        </WarningList>
      )}
      {error === EErrorType.ERROR_AMOUNT_MAX && (
        <WarningList>
          <WarningListIcon>
            <AttentionRequiredDocuments width={26} height={26} />
          </WarningListIcon>
          <TextL weight="bold">Valor superior ao valor da operação.</TextL>
          <WarningListItems>
            <WarningListItem>
              A soma do valor solicitado deve ser 100% (
              {formatCurrency(simulation!.requested_amount!, true)}).
            </WarningListItem>
          </WarningListItems>
        </WarningList>
      )}
    </BusinessEditSideSheet>
  );
};

export default SplitPaymentEditSideSheet;
